import { useResolutionInfo } from '@mm/hooks';
import classNames from 'classnames';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { TABLET_SMALL } from '../../../../editor/constants/screenSize';
import { MissedLeadsTimer } from '../MissedLeadsTimer/MissedLeadsTimer';
import { HEADER_STEPS, AVERAGE_WEB_TRAFFIC_GROWTH } from './constants';
import { useWebsiteReportModals } from './use-website-report-modals';
import metaLogo from 'assets/meta-partner.png';
import iconiqLogo from 'assets/iconiq-logo.png';
import top50Logo from 'assets/g2-top50-logo.png';
import styles from './WebsiteReportSummaryVersionB.module.css';
import { ROIAnalysisPanel } from '../ROIAnalysisPanel/ROIAnalysisPanel';
import { HowXRayWorksVersionB } from '../HowXRayWorksVersionB/HowXRayWorksVersionB';
import { BarGraph } from '../MissedOpportunities/MissedOpportunities';
import { EditOutlined } from '@ant-design/icons';
import speedometerSrc from './Icons/speedometer.png';
import downArrow from './Icons/down-arrow.png';
import rightArrow from './Icons/right-arrow.png';
import { WebsiteScreenshot } from '../WebsiteScreenshot/WebsiteScreenshot';
import { Magnifier } from './Magnifier';

// tslint:disable-next-line: cyclomatic-complexity
export const WebsiteReportSummaryVersionB = () => {
  const {
    openSignupModal,
    openEditUrlModal,
    websiteURL,
    monthlyTrafficRef,
    showPaidTrafficROI,
    activeStep,
    totalTraffic,
    setTotalTraffic,
  } = useWebsiteReportModals();

  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= TABLET_SMALL;
  const dateNow = moment();
  const [canResolveDomain, setCanResolveDomain] = useState(true);

  // Indicates that the DNS query was unable to find any records for the specified domain. This is known as a NXDOMAIN (Non-Existent Domain) error.
  const NXDOMAIN_ERROR_CODE = 3;

  const organicTrafficTotal = totalTraffic.trafficTypes?.organic || 0;
  const paidTrafficTotal = totalTraffic.trafficTypes?.paid || 0;
  const trafficSum = organicTrafficTotal + paidTrafficTotal;
  const screenshotSrc = `https://image.thum.io/get/width/384/https://${websiteURL}`;

  const missedTotalLeads =
    (organicTrafficTotal + paidTrafficTotal) * AVERAGE_WEB_TRAFFIC_GROWTH;

  useEffect(() => {
    (async () => {
      try {
        // Checking if domain name can be resolved, https://developers.google.com/speed/public-dns/docs/doh/json
        const response = await fetch(
          `https://dns.google/resolve?name=${websiteURL}`,
        );
        const json = await response.json();
        if (json.Status === NXDOMAIN_ERROR_CODE) {
          setCanResolveDomain(false);
        }
      } catch (err) {}
    })();
  }, [websiteURL]);

  return (
    <div className={classNames(styles.wrapper)} id="website-report-scroll">
      {!isMobile && (
        <div className={classNames(styles.row, styles.headerContainer)}>
          <div className={classNames(styles.row, styles.headerContent)}>
            <div className={classNames(styles.row, styles.steps)}>
              {HEADER_STEPS.map(item => {
                return (
                  <React.Fragment key={item.step}>
                    <div
                      key={item.step}
                      className={classNames(styles.step, {
                        [styles.inactiveStep]: activeStep !== item.step,
                      })}
                    >
                      {item.title}
                    </div>
                    {item.step !== HEADER_STEPS.length && (
                      <div className={styles.dashes} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className={styles.container}>
        <div className={classNames(styles.row, styles.logos)}>
          <img src={iconiqLogo} className={styles.logo} />
          <img src={top50Logo} className={styles.logo} />
          <img src={metaLogo} className={styles.meta} />
        </div>

        <section className={classNames(styles.mainPanel, styles.section)}>
          <div className={classNames(styles.column, styles.title)}>
            <div className={styles.heading}>
              <div className={styles.websiteUrl}>{websiteURL}</div>
              Capture Rate Can Be Improved
            </div>
            <div className={styles.subheading}>
              Based on our analysis your website is capturing the contact
              information of 2% of your website visitors. Our system can{' '}
              <span className={styles.importantHighligted}>
                increase conversion to 20%.
              </span>
            </div>
          </div>
          <div className={classNames(styles.row, styles.websiteOverview)}>
            <div className={styles.left}>
              <div className={styles.websiteUrl}>
                {websiteURL}{' '}
                <EditOutlined
                  className={styles.editIcon}
                  onClick={openEditUrlModal}
                />
              </div>
              <div className={styles.screenshotBlock}>
                <WebsiteScreenshot
                  canResolve={canResolveDomain}
                  websiteURL={websiteURL}
                  src={screenshotSrc}
                />
                <Magnifier
                  organicTraffic={organicTrafficTotal}
                  paidTraffic={paidTrafficTotal}
                  className={styles.magnifier}
                />
                <div
                  className={classNames(styles.column, styles.ratingScoreBlock)}
                >
                  <div
                    className={classNames(
                      styles.column,
                      styles.gap16,
                      styles.ratingScore,
                    )}
                  >
                    Rating Score
                    <img
                      src={speedometerSrc}
                      alt="Poor rating score"
                      className={styles.speedometer}
                    />
                  </div>
                  <div className={styles.ratingScoreDescription}>
                    <span className={styles.ratingTextDecorated}>
                      {websiteURL}
                    </span>{' '}
                    {''}
                    {''}website capture rate is in the{' '}
                    <span className={styles.ratingTextDecorated}>
                      bottom 10%
                    </span>{' '}
                    of comparable businesses.
                  </div>
                </div>
              </div>
            </div>
            <img src={rightArrow} className={styles.rightArrow} />
            <div className={styles.right}>
              <BarGraph
                className={styles.barGraph}
                buttonName="Get Started"
                onClick={openSignupModal}
                paidTraffic={paidTrafficTotal}
                organicTraffic={organicTrafficTotal}
                showTotal
              />
              <div className={classNames(styles.column, styles.promo)}>
                <span className={styles.importantHighligted}>
                  Without Popups or Widgets!
                </span>
                <span className={styles.text}>
                  We capture up to 20% of the contact information from your
                  website traffic.
                </span>
                <span className={classNames(styles.text, styles.bold600)}>
                  With no user interaction!
                </span>
              </div>
              <img src={downArrow} className={styles.downArrow} />
            </div>
          </div>
        </section>

        <section className={styles.section}>
          <ROIAnalysisPanel
            website={websiteURL}
            isOrganic
            traffic={totalTraffic}
            setTotalTraffic={setTotalTraffic}
            trafficByMonth={monthlyTrafficRef.current}
            onClick={openSignupModal}
          />
        </section>

        <section className={classNames(styles.section, styles.marginTop24)}>
          {showPaidTrafficROI && (
            <ROIAnalysisPanel
              website={websiteURL}
              isOrganic={false}
              traffic={totalTraffic}
              setTotalTraffic={setTotalTraffic}
              trafficByMonth={monthlyTrafficRef.current}
              onClick={openSignupModal}
            />
          )}
        </section>

        <section
          className={classNames(
            styles.previewBlock,
            styles.column,
            styles.section,
          )}
        >
          <HowXRayWorksVersionB
            websiteURL={websiteURL}
            currentTraffic={trafficSum}
            additionalLeads={missedTotalLeads}
            onClick={openSignupModal}
            src={screenshotSrc}
            canResolveDomain={canResolveDomain}
          />
        </section>

        <section className={classNames(styles.timer, styles.section)}>
          <div className={classNames(styles.row, styles.timerContent)}>
            <div className={styles.left}>
              <div className={styles.note}>greatness awaits</div>
              <div className={styles.heading}>
                Every visitor is a potential lead!
              </div>
              <div className={styles.subHeading}>
                This clock was activated at {moment().format('h:mm:ss a')} on{' '}
                {moment(dateNow).format('MMMM Do YYYY')} and represents leads
                and their contact information you could have captured by
                installing our tag on your website.
              </div>
            </div>
            <div className={styles.right}>
              <MissedLeadsTimer
                totalTraffic={totalTraffic}
                onClick={openSignupModal}
                buttonName="Get Started!"
                css={{
                  button: styles.timerButton,
                  tooltip: styles.missedLeadsTooltip,
                }}
                withTooltip
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};
